// 自动识别「常见模块」并打上 reveal 动画类,实现「每个代码模块都自动有动画」。
// 路由切换后调用 initReveal(root) 即可生效;既支持手动加 class,也支持自动发现。

const REVEAL_SELECTOR = '.reveal, .reveal-fade-up, .reveal-fade-left, .reveal-fade-right, .reveal-zoom, .reveal-fade'

// 自动动画规则:命中选择器时,如果元素本身没有 reveal 类,就给它补一个,
// 并根据规则选择动画变体。同一元素只打一次。
const AUTO_RULES = [
  // 大区块整体淡入上移
  { selector: 'section.section, .section, [class*="section-"]', variant: 'reveal-fade-up', skipIfHas: REVEAL_SELECTOR },

  // 章节头部(标题 + 副标题)
  { selector: '.section-head, .section__head, .section-head-center, .section__head-copy', variant: 'reveal-fade-up' },
  { selector: '.hero-text, .hero-copy, .hero-container, .panel-left, .panel-text, .faq-intro, .process-content, .work-content', variant: 'reveal-fade-left' },
  { selector: '.hero-illustration, .hero-art, .panel-right, .panel-illustration, .process-illustration, .work-illustration', variant: 'reveal-fade-right' },
  { selector: '.hero-eyebrow, .section-eyebrow, .section__eyebrow, .section-subtitle, .section__lead, .section-lead, .section-desc, .panel-desc, .panel-tag', variant: 'reveal-fade' },

  // 卡片网格 - 交错放大
  { selector: '.service-card, .step-card, .project-card, .principle-card, .feature-card, .stat-card, .card-grid > *, .card, .work-card, .tech-item, .journey-step-item, .match-card, .pricing-card, .industry-card, .reason-card, .reason-num, .featured-card, .latest-card, .insight-card', variant: 'reveal-zoom' },
  { selector: '.service-mobile-item, .process-tab-item, .tab-btn, .tab-item, .tab, .work-filter button, .filter-btn', variant: 'reveal-fade-up' },
  { selector: '.service-card', variant: 'reveal-zoom' },
  { selector: '.projects-grid > *, .services-grid > *, .principles-grid > *, .steps-grid > *, .features-grid > *, .cards-grid > *, .work-cards > *, .process-steps > *, .step-item, .feature-item, .list-item, .tech-grid > *, .match-right > *, .journey-content > *, .industries-grid > *, .reasons-grid > *, .insight-grid > *, .pricing-grid > *', variant: 'reveal-fade-up' },

  // 步骤 / 列表项
  { selector: '.process-content-wrap, .process-wrap, .work-wrap, .feature-item, .lifecycle-item, .list-item, li.reveal-auto, .journey-left, .journey-right, .match-left, .match-right, .form-left, .form-right, .panel-left, .panel-right', variant: 'reveal-fade-left' },

  // 大图片
  { selector: '.illust-placeholder, .hero-img, .feature-img, .mv-image-wrap, .illustration-img, .card-phone img, .journey-illust, .hero-art, .hero-illustration', variant: 'reveal-fade-right' },

  // 按钮组
  { selector: '.btn-group, .bottom-btn, .cta-wrap, .cta-buttons-section .btn, .project-buttons, .process-buttons, .view-all-wrap, .steps-bottom, .journey-head', variant: 'reveal-fade-up' },

  // 表单
  { selector: '.form-card, .form-section .form-wrap, .form-layout, .form-left, .form-right', variant: 'reveal-fade-up' },

  // Logos / 评价
  { selector: '.logo-item, .logo-row > *, .confianza-logos > *', variant: 'reveal-fade-up' },
  { selector: '.badge-item, .award-badge, .review-block', variant: 'reveal-zoom' },

  // 单条 FAQ / 段落
  { selector: '.faq-item, .faq-list > *, .accordion-item, .faq-list article', variant: 'reveal-fade-up' },

  // CTA / banner
  { selector: '.cta-panel, .cta-section, .cta-card, .page-banner, .banner', variant: 'reveal-fade-up' },

  // 标题文本
  { selector: '.hero h1, .hero-title, .hero-container h1, h2, h3', variant: 'reveal-fade-up' }
]

// 单例观察者,避免每次切路由都新建
let _pageObserver = null
let _observedSet = new WeakSet()

function applyAutoReveal (root) {
  if (!root) return
  AUTO_RULES.forEach((rule) => {
    root.querySelectorAll(rule.selector).forEach((el) => {
      // 已有 reveal 类就跳过
      if (el.classList && el.classList.contains('reveal')) return
      const skipCheck = rule.skipIfHas
      if (skipCheck && el.matches && el.matches(skipCheck)) return
      // 已经手动设置了任意 reveal-* 变体也跳过
      const hasManual = ['reveal-fade-up', 'reveal-fade-left', 'reveal-fade-right', 'reveal-zoom', 'reveal-fade']
        .some((c) => el.classList && el.classList.contains(c))
      if (hasManual) return
      // 跳过 fixed/sticky 定位的元素(避免动画 transform 影响 fixed 渲染)
      const cs = window.getComputedStyle(el)
      if (cs.position === 'fixed' || cs.position === 'sticky') return
      // 跳过正在播放动画的元素
      if (cs.animationName && cs.animationName !== 'none') return
      el.classList.add(rule.variant)
    })
  })
}

// 路由切换后,从外部调用,用于页面没使用 mixin 的情况(比如 Shell 容器)
// 使用单例 observer,自动清理已消失的节点
export function initPageReveal (root = document.body) {
  if (typeof window === 'undefined') return
  applyAutoReveal(root)
  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // 收集当前所有待观察节点(只取尚未 visible 且尚未被本观察者绑定过的)
  const nodes = root.querySelectorAll(REVEAL_SELECTOR)
  const fresh = []
  nodes.forEach((n) => {
    if (n.classList.contains('is-visible')) return
    if (_observedSet.has(n)) return
    fresh.push(n)
  })

  if (reduced) {
    fresh.forEach((n) => n.classList.add('is-visible'))
    return
  }

  if (typeof IntersectionObserver === 'undefined') {
    fresh.forEach((n) => n.classList.add('is-visible'))
    return
  }

  if (!_pageObserver) {
    _pageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          _pageObserver.unobserve(entry.target)
          _observedSet.delete(entry.target)
        }
      })
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
  }
  fresh.forEach((n) => {
    _observedSet.add(n)
    _pageObserver.observe(n)
  })
}

function buildObserver (root, selector) {
  if (typeof IntersectionObserver === 'undefined') return null
  const reduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduced) {
    root.querySelectorAll(selector).forEach((n) => n.classList.add('is-visible'))
    return null
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
  root.querySelectorAll(selector).forEach((n) => observer.observe(n))
  return observer
}

export default {
  methods: {
    initReveal (selector = REVEAL_SELECTOR, refName = 'page', { auto = true } = {}) {
      this.$nextTick(() => {
        const root = this.$refs[refName] || this.$el || document
        if (!root) return
        if (auto) applyAutoReveal(root)
        const observer = buildObserver(root, selector)
        if (observer) this._revealObserver = observer
      })
    }
  },
  mounted () { this.initReveal() },
  beforeDestroy () {
    if (this._revealObserver) {
      this._revealObserver.disconnect()
      this._revealObserver = null
    }
  }
}

// 兼容旧代码,如果其他文件直接 require 它也能拿到 mixin
export { REVEAL_SELECTOR, applyAutoReveal, buildObserver }
